import {
  Alert,
  Box,
  Button,
  Card,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  Portal,
  Table,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { Check, Minus, Pencil, Plus, Trash2, Users } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import type { ToastLike, TranslateFn } from '@mangovoice/mango-ui'
import {
  ConfirmDialog,
  ListToolbar,
  LoadingState,
  usePagination,
} from '@mangovoice/mango-ui'

import type { UserGroupApi } from '../api/userGroupApi'
import {
  useDeleteUserGroup,
  UserGroupError,
} from '../hooks/useUserGroupMutations'
import { useListUserGroups, usePbxUsers } from '../hooks/useUserGroups'
import { UserGroupsI18nProvider, useUserGroupsI18n } from '../i18n'
import type { UserGroupsTranslationLike } from '../i18n'
import type { UserGroup } from '../types/userGroup.types'

export interface UserGroupListProps {
  api: UserGroupApi
  pbxId: number
  onNavigateToEdit: (groupId: number) => void
  onNavigateToAdd: () => void
  canCreate: boolean
  canEdit: boolean
  canDelete: boolean
  toast?: ToastLike
  t?: UserGroupsTranslationLike
  locale?: string
}

// The list endpoint has no server-side search, so we fetch the full set for the
// PBX in one page and paginate/search client-side. This limit is a safety
// ceiling well above any real PBX's group count; if it's ever exceeded we warn
// rather than silently truncate (see truncation guard below).
const USER_GROUP_FETCH_LIMIT = 1000

// ─── Helpers ──────────────────────────────────────────────────────────────────

const filterUserGroups = (
  groups: UserGroup[],
  searchTerm: string,
  memberNamesByGroupId: Map<number, string[]>,
): UserGroup[] => {
  if (!searchTerm.trim()) return groups
  const normalized = searchTerm.toLowerCase().trim()
  return groups.filter(({ id, name }) => {
    if (name.toLowerCase().includes(normalized)) return true
    const memberNames = memberNamesByGroupId.get(id) ?? []
    return memberNames.some((member) =>
      member.toLowerCase().includes(normalized),
    )
  })
}

const USER_GROUP_PAGE_SIZE_OPTIONS = [25, 50, 100]

const TOOLBAR_FALLBACKS: Record<string, string> = {
  'userGroups.list.pagination.resultsPerPage': 'Results per page:',
  'userGroups.list.pagination.previousPage': 'Previous page',
  'userGroups.list.pagination.nextPage': 'Next page',
  'userGroups.list.pagination.jumpToPage': 'Jump to page {{page}}',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

const GroupsLoadingState: React.FC = () => {
  const { text } = useUserGroupsI18n()

  return (
    <LoadingState
      layout="alert"
      title={text('userGroups.list.loading', 'Loading user groups...')}
    />
  )
}

const ErrorState: React.FC<{ message: string }> = ({ message }) => {
  const { text } = useUserGroupsI18n()

  return (
    <Alert.Root status="error" p={4}>
      <Alert.Indicator />
      <Box>
        <Text fontWeight="bold">
          {text('userGroups.common.error', 'Error')}
        </Text>
        <Text>{message}</Text>
      </Box>
    </Alert.Root>
  )
}

const EmptyState: React.FC<{ hasSearchTerm?: boolean }> = ({
  hasSearchTerm = false,
}) => {
  const { text } = useUserGroupsI18n()

  return (
    <VStack
      w="100%"
      justifyContent="center"
      alignItems="center"
      gap={4}
      py={16}
    >
      <Icon boxSize={16} color={hasSearchTerm ? 'fg.muted' : 'blue.solid'}>
        {hasSearchTerm ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
        ) : (
          <Users />
        )}
      </Icon>
      <VStack gap={1}>
        <Text fontWeight="semibold" fontSize="lg">
          {hasSearchTerm
            ? text('userGroups.list.noResultsTitle', 'No results found')
            : text('userGroups.list.emptyTitle', 'No user groups yet')}
        </Text>
        <Text color="fg.muted" fontSize="sm">
          {hasSearchTerm
            ? text(
                'userGroups.list.noResultsDescription',
                'Try adjusting your search terms',
              )
            : text(
                'userGroups.list.emptyDescription',
                'Get started by creating your first user group',
              )}
        </Text>
      </VStack>
    </VStack>
  )
}

const SearchInput: React.FC<{
  value: string
  onChange: (v: string) => void
}> = ({ value, onChange }) => {
  const { text } = useUserGroupsI18n()
  // Use a unique per-instance id so the input has a stable id/name for the
  // browser (autofill heuristics warn otherwise) without risking duplicate
  // ids if this list ever renders more than once on a page.
  const inputId = React.useId()
  return (
    <Box w={{ base: '100%', md: '307px' }} position="relative">
      <Icon
        boxSize={4}
        color="fg.muted"
        position="absolute"
        left={3}
        top="50%"
        transform="translateY(-50%)"
        zIndex={1}
        pointerEvents="none"
        aria-hidden="true"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.35-4.35" />
        </svg>
      </Icon>
      <Input
        id={inputId}
        name="user-group-search"
        placeholder={text(
          'userGroups.list.searchPlaceholder',
          'Search by name or member...',
        )}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="md"
        h="40px"
        pl={10}
        bg="bg.subtle"
        borderColor="border.default"
        borderRadius="md"
        _placeholder={{ color: 'fg.subtle' }}
        _hover={{ borderColor: 'border.emphasized' }}
        _focusVisible={{
          borderColor: 'brand.solid',
          boxShadow: '0 0 0 2px var(--chakra-colors-brand-solid)',
          outline: 'none',
        }}
        aria-label={text(
          'userGroups.list.searchAriaLabel',
          'Search user groups',
        )}
      />
    </Box>
  )
}

const AccessIndicator: React.FC<{ value: number; label: string }> = ({
  value,
  label,
}) => {
  const { text } = useUserGroupsI18n()

  if (value === 2) {
    return (
      <Box
        as="span"
        px={2}
        py={0.5}
        borderRadius="sm"
        borderWidth="1px"
        borderColor="blue.muted"
        color="blue.fg"
        fontSize="xs"
        fontWeight="medium"
      >
        {text('userGroups.common.pin', 'Pin')}
      </Box>
    )
  }

  const isEnabled = value === 1
  const status = isEnabled
    ? text('userGroups.common.yes', 'Yes')
    : text('userGroups.common.no', 'No')

  return (
    <Box
      w="24px"
      h="24px"
      borderRadius="full"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      bg={isEnabled ? 'green.solid' : 'gray.subtle'}
      color={isEnabled ? 'green.contrast' : 'fg.muted'}
      aria-label={text('userGroups.list.accessAria', '{{label}}: {{status}}', {
        label,
        status,
      })}
      role="img"
    >
      <Icon as={isEnabled ? Check : Minus} boxSize={3} strokeWidth={2.25} />
    </Box>
  )
}

interface UserGroupRowProps {
  group: UserGroup
  memberNames: string[]
  onEditClick: (id: number) => void
  onDeleteClick: (id: number, name: string) => void
  canEdit: boolean
  canDelete: boolean
}

const UserGroupRow: React.FC<UserGroupRowProps> = ({
  group,
  memberNames,
  onEditClick,
  onDeleteClick,
  canEdit,
  canDelete,
}) => {
  const { text } = useUserGroupsI18n()
  const memberCount = group.users.length

  return (
    <Table.Row _hover={{ bg: 'bg.subtle' }}>
      <Table.Cell px={4} py={3}>
        <HStack gap={1.5}>
          <Text
            as="button"
            fontWeight="medium"
            fontSize="xs"
            color="blue.solid"
            _hover={{ textDecoration: 'underline' }}
            _focusVisible={{
              textDecoration: 'underline',
              outline: '2px solid',
              outlineColor: 'blue.500',
              outlineOffset: '2px',
              borderRadius: 'sm',
            }}
            onClick={() => onEditClick(group.id)}
            cursor="pointer"
            background="none"
            border="none"
            p={0}
          >
            {group.name}
          </Text>
          {group.auto_generated && (
            <Text fontSize="xs" color="fg.muted">
              ({text('userGroups.common.auto', 'auto')})
            </Text>
          )}
        </HStack>
      </Table.Cell>
      <Table.Cell px={4} py={3} maxW="360px">
        <VStack align="start" gap={0.5}>
          <Text fontSize="xs" color="fg.muted">
            {memberCount}
          </Text>
          {memberNames.length > 0 && (
            <Text fontSize="xs" color="fg.subtle" lineClamp={3}>
              {memberNames.join(', ')}
            </Text>
          )}
        </VStack>
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <AccessIndicator
          value={group.internal_access}
          label={text('userGroups.list.column.internal', 'Internal')}
        />
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <AccessIndicator
          value={group.local_access}
          label={text('userGroups.list.column.local', 'Local')}
        />
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <AccessIndicator
          value={group.domestic_long_distance_access}
          label={text(
            'userGroups.list.column.domesticLongDistance',
            'Long distance',
          )}
        />
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <AccessIndicator
          value={group.international_access}
          label={text('userGroups.list.column.international', 'International')}
        />
      </Table.Cell>
      <Table.Cell px={4} py={3}>
        <Box
          display="flex"
          gap={2}
          justifyContent="flex-end"
          role="group"
          aria-label={text(
            'userGroups.list.actionsForGroup',
            'Actions for {{name}}',
            {
              name: group.name,
            },
          )}
        >
          {canEdit && (
            <IconButton
              size="xs"
              variant="subtle"
              colorPalette="neutral"
              aria-label={text('userGroups.list.editGroup', 'Edit {{name}}', {
                name: group.name,
              })}
              onClick={() => onEditClick(group.id)}
              w="32px"
              h="32px"
              _focusVisible={{
                ring: '2px',
                ringColor: 'blue.500',
                ringOffset: '2px',
              }}
            >
              <Pencil size={16} />
            </IconButton>
          )}
          {canDelete && !group.auto_generated ? (
            <IconButton
              size="xs"
              colorPalette="error"
              variant="subtle"
              onClick={() => onDeleteClick(group.id, group.name)}
              aria-label={text(
                'userGroups.list.deleteGroup',
                'Delete {{name}}',
                {
                  name: group.name,
                },
              )}
              w="32px"
              h="32px"
              _focusVisible={{
                ring: '2px',
                ringColor: 'red.500',
                ringOffset: '2px',
              }}
            >
              <Trash2 size={16} />
            </IconButton>
          ) : canDelete && group.auto_generated ? (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <IconButton
                  size="xs"
                  colorPalette="error"
                  variant="subtle"
                  disabled
                  aria-label={text(
                    'userGroups.list.deleteGroupDisabled',
                    'Delete {{name}} (disabled)',
                    { name: group.name },
                  )}
                  w="32px"
                  h="32px"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip.Trigger>
              <Portal>
                <Tooltip.Positioner>
                  <Tooltip.Content>
                    {text(
                      'userGroups.list.autoGeneratedDeleteTooltip',
                      'Auto-generated groups cannot be deleted',
                    )}
                  </Tooltip.Content>
                </Tooltip.Positioner>
              </Portal>
            </Tooltip.Root>
          ) : null}
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export const UserGroupList: React.FC<UserGroupListProps> = ({
  t,
  locale,
  ...props
}) => (
  <UserGroupsI18nProvider t={t} locale={locale}>
    <UserGroupListInner {...props} />
  </UserGroupsI18nProvider>
)

type UserGroupListInnerProps = Omit<UserGroupListProps, 't' | 'locale'>

const UserGroupListInner: React.FC<UserGroupListInnerProps> = ({
  api,
  pbxId,
  onNavigateToEdit,
  onNavigateToAdd,
  canCreate,
  canEdit,
  canDelete,
  toast,
}) => {
  const { text } = useUserGroupsI18n()
  const isValidPbxId = !Number.isNaN(pbxId) && pbxId > 0

  const [searchTerm, setSearchTerm] = useState('')
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isOpen: boolean
    groupId: number | null
    groupName: string
  }>({ isOpen: false, groupId: null, groupName: '' })

  const { userGroups, pagination, isLoading, error } = useListUserGroups({
    api,
    pbxId,
    params: { limit: USER_GROUP_FETCH_LIMIT },
    queryOptions: { enabled: isValidPbxId },
  })

  // Surface truncation rather than silently hiding groups: if a PBX ever has
  // more groups than we fetch, the client-side list/search would be incomplete.
  const isTruncated = pagination.count > userGroups.length

  const { pbxUsers } = usePbxUsers({
    api,
    pbxId,
    queryOptions: { enabled: isValidPbxId },
  })

  const userNamesById = useMemo(() => {
    const map = new Map<number, string>()
    pbxUsers.forEach(({ user }) => {
      const displayName =
        user.name || `${user.first_name} ${user.last_name}`.trim()
      map.set(user.id, displayName)
    })
    return map
  }, [pbxUsers])

  const memberNamesByGroupId = useMemo(() => {
    const map = new Map<number, string[]>()
    userGroups.forEach((group) => {
      const names = group.users
        .map((userId) => userNamesById.get(userId))
        .filter((n): n is string => !!n)
        .sort((a, b) => a.localeCompare(b))
      map.set(group.id, names)
    })
    return map
  }, [userGroups, userNamesById])

  const filteredGroups = useMemo(
    () => filterUserGroups(userGroups, searchTerm, memberNamesByGroupId),
    [userGroups, searchTerm, memberNamesByGroupId],
  )

  const filteredCount = filteredGroups.length
  const { pageSize, setPageSize, currentPage, setCurrentPage, offset, limit } =
    usePagination(filteredCount)

  useEffect(() => {
    setCurrentPage(1)
  }, [filteredCount, setCurrentPage])
  const paginatedGroups = useMemo(
    () => filteredGroups.slice(offset, offset + limit),
    [filteredGroups, offset, limit],
  )
  const totalCount = filteredGroups.length
  const toolbarT: TranslateFn = useCallback(
    (key, opts) =>
      text(
        key,
        TOOLBAR_FALLBACKS[key] ?? key,
        opts as Record<string, string | number> | undefined,
      ),
    [text],
  )

  const deleteUserGroupMutation = useDeleteUserGroup({ api, pbxId })

  const handleDeleteClick = useCallback((id: number, name: string) => {
    setDeleteDialogState({ isOpen: true, groupId: id, groupName: name })
  }, [])

  const handleDeleteDialogClose = useCallback(() => {
    setDeleteDialogState({ isOpen: false, groupId: null, groupName: '' })
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteDialogState.groupId) return

    deleteUserGroupMutation.mutate(
      { id: deleteDialogState.groupId },
      {
        onSuccess: () => {
          toast?.create({
            title: text(
              'userGroups.list.deleteDialog.successTitle',
              'User group deleted',
            ),
            description: text(
              'userGroups.list.deleteDialog.successDescription',
              '"{{name}}" has been deleted.',
              { name: deleteDialogState.groupName },
            ),
            type: 'success',
            duration: 4000,
          })
          handleDeleteDialogClose()
        },
        onError: (err) => {
          if (err instanceof UserGroupError && err.code === 'not_found') {
            toast?.create({
              title: text(
                'userGroups.list.deleteDialog.alreadyDeletedTitle',
                'Already deleted',
              ),
              description: text(
                'userGroups.list.deleteDialog.alreadyDeletedDescription',
                'This user group was not found - it may have already been deleted.',
              ),
              type: 'info',
              duration: 4000,
            })
            handleDeleteDialogClose()
          } else if (
            err instanceof UserGroupError &&
            err.code === 'forbidden'
          ) {
            toast?.create({
              title: text(
                'userGroups.toast.authRequiredTitle',
                'Authentication required',
              ),
              description: text(
                'userGroups.toast.authRequiredDescription',
                'Your session may have expired. Please re-authenticate and try again.',
              ),
              type: 'error',
              duration: 6000,
            })
          } else {
            toast?.create({
              title: text('userGroups.common.error', 'Error'),
              description:
                err.message ||
                text(
                  'userGroups.list.deleteDialog.failedDescription',
                  'Failed to delete user group.',
                ),
              type: 'error',
              duration: 5000,
            })
          }
        },
      },
    )
  }, [
    deleteDialogState,
    deleteUserGroupMutation,
    toast,
    handleDeleteDialogClose,
    text,
  ])

  const showPagination = filteredGroups.length > 0 && !isLoading && !error

  return (
    <Card.Root
      bg="bg.default"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      p={4}
      gap={4}
    >
      <Card.Header p={0}>
        <Box
          display="flex"
          minH="40px"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={{ base: 'column', md: 'column', lg: 'row' }}
          gap={4}
        >
          <Box alignSelf={{ base: 'flex-start', lg: 'auto' }}>
            <Heading
              as="h1"
              fontSize="2xl"
              fontWeight="extrabold"
              color="fg.default"
            >
              {text('userGroups.list.title', 'User Groups')}
            </Heading>
          </Box>
          <HStack
            gap={6}
            justifyContent={{
              base: 'space-between',
              md: 'flex-start',
              lg: 'flex-end',
            }}
            width={{ base: '100%', md: '100%', lg: 'auto' }}
            flexShrink={{ base: 1, md: 0 }}
            alignItems="center"
            flexWrap={{ base: 'wrap', md: 'nowrap' }}
          >
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
            {canCreate && (
              <Button
                onClick={onNavigateToAdd}
                size="sm"
                h="36px"
                bg="blue.solid"
                color="blue.contrast"
                borderRadius="md"
                flexShrink={0}
                whiteSpace="nowrap"
                _hover={{ bg: 'blue.emphasized' }}
                _active={{ bg: 'blue.emphasized' }}
                css={{
                  '& svg': { stroke: 'var(--chakra-colors-blue-contrast)' },
                }}
                px={4}
                disabled={!isValidPbxId}
                aria-label={text(
                  'userGroups.list.createNewAriaLabel',
                  'Create new user group',
                )}
              >
                <Plus size={16} />
                {text('userGroups.list.createNew', 'New User Group')}
              </Button>
            )}
          </HStack>
        </Box>
      </Card.Header>

      <Card.Body p={0}>
        {!isLoading && !error && isTruncated && (
          <Alert.Root status="warning" mb={4}>
            <Alert.Indicator />
            <Box>
              <Text>
                {text(
                  'userGroups.list.truncatedWarning',
                  'Showing the first {{shown}} of {{total}} user groups. Some groups are not listed — please contact support.',
                  { shown: userGroups.length, total: pagination.count },
                )}
              </Text>
            </Box>
          </Alert.Root>
        )}
        {isLoading && <GroupsLoadingState />}
        {!isLoading && error && (
          <ErrorState message={(error as Error).message} />
        )}
        {!isLoading && !error && !filteredGroups.length && (
          <EmptyState hasSearchTerm={searchTerm.trim().length > 0} />
        )}
        {!isLoading && !error && filteredGroups.length > 0 && (
          <Box
            overflowX="auto"
            role="region"
            aria-label={text(
              'userGroups.list.tableAriaLabel',
              'User groups table',
            )}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="lg"
          >
            <Table.Root variant="line">
              <Table.Header>
                <Table.Row bg="gray.subtle">
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.name', 'Name')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.members', 'Members')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.internal', 'Internal')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.local', 'Local')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.longDistance', 'Long Dist.')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text(
                      'userGroups.list.column.international',
                      'International',
                    )}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader
                    px={4}
                    py={3}
                    scope="col"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {text('userGroups.list.column.action', 'Action')}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {paginatedGroups.map((group) => (
                  <UserGroupRow
                    key={group.id}
                    group={group}
                    memberNames={memberNamesByGroupId.get(group.id) ?? []}
                    onEditClick={onNavigateToEdit}
                    onDeleteClick={handleDeleteClick}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
        <ListToolbar
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            if (size !== 'All') setPageSize(size)
          }}
          currentPage={currentPage}
          totalCount={totalCount}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
          showToolbar={showPagination}
          showAllOption={false}
          pageSizeOptions={USER_GROUP_PAGE_SIZE_OPTIONS}
          size="sm"
          t={toolbarT}
          i18nPrefix="userGroups.list.pagination"
          summary={
            <Text fontSize="sm" color="fg.subtle">
              {text('userGroups.list.pagination.total', '({{count}} total)', {
                count: totalCount,
              })}
            </Text>
          }
        />
      </Card.Body>

      <ConfirmDialog
        open={deleteDialogState.isOpen}
        onClose={handleDeleteDialogClose}
        title={text('userGroups.list.deleteDialog.title', 'Delete User Group')}
        description={text(
          'userGroups.list.deleteDialog.description',
          'Are you sure you want to delete "{{name}}"? This will remove all member associations and cannot be undone.',
          { name: deleteDialogState.groupName },
        )}
        confirmLabel={text('userGroups.common.delete', 'Delete')}
        cancelLabel={text('userGroups.common.cancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteUserGroupMutation.isPending}
      />
    </Card.Root>
  )
}
